import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ExportControls = ({ currentTrack, onExport, onShare }) => {
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('mp3');
  const [exportQuality, setExportQuality] = useState('high');
  const [exportName, setExportName] = useState('');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const exportFormats = [
    { id: 'mp3', label: 'MP3', description: 'Universal compatibility', icon: 'Music' },
    { id: 'wav', label: 'WAV', description: 'Uncompressed audio', icon: 'Waves' },
    { id: 'flac', label: 'FLAC', description: 'Lossless compression', icon: 'Disc' },
    { id: 'midi', label: 'MIDI', description: 'Musical notation data', icon: 'FileMusic' }
  ];

  const qualityOptions = [
    { id: 'low', label: 'Low (128 kbps)', size: '~3 MB' },
    { id: 'medium', label: 'Medium (192 kbps)', size: '~4.5 MB' },
    { id: 'high', label: 'High (320 kbps)', size: '~7.5 MB' },
    { id: 'lossless', label: 'Lossless', size: '~25 MB' }
  ];

  const shareOptions = [
    { id: 'link', label: 'Share Link', icon: 'Link', color: 'primary' },
    { id: 'social', label: 'Social Media', icon: 'Share2', color: 'secondary' },
    { id: 'email', label: 'Email', icon: 'Mail', color: 'accent' },
    { id: 'embed', label: 'Embed Code', icon: 'Code', color: 'success' }
  ];

  const handleExportStart = async () => {
    setIsExporting(true);
    setExportProgress(0);

    // Simulate export progress
    const interval = setInterval(() => {
      setExportProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsExporting(false);
          setIsExportModalOpen(false);
          onExport({
            format: exportFormat,
            quality: exportQuality,
            name: exportName || currentTrack,
            metadata: includeMetadata
          });
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleShare = (shareType) => {
    onShare(shareType, {
      track: currentTrack,
      format: exportFormat
    });
  };

  const generateDefaultName = () => {
    const date = new Date().toISOString().split('T')[0];
    return `${currentTrack.replace(/\s+/g, '_')}_${date}`;
  };

  return (
    <div className="flex items-center space-x-2">
      {/* Quick Share Buttons */}
      <Button
        variant="ghost"
        iconName="Share"
        onClick={() => handleShare('link')}
        className="w-10 h-10 p-0"
      />
      
      {/* Export Button */}
      <Button
        variant="primary"
        iconName="Download"
        onClick={() => {
          setExportName(generateDefaultName());
          setIsExportModalOpen(true);
        }}
      >
        Export
      </Button>

      {/* Export Modal */}
      {isExportModalOpen && (
        <div className="fixed inset-0 z-modal bg-background/80 backdrop-blur-cosmic flex items-center justify-center p-4">
          <div className="w-full max-w-md glass bg-surface/95 backdrop-blur-cosmic rounded-xl border border-border">
            {/* Header */}
            <div className="p-6 border-b border-border/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                    <Icon name="Download" size={16} color="white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-text-primary">
                      Export Audio
                    </h3>
                    <p className="text-xs text-text-secondary">
                      Save your planetary composition
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  iconName="X"
                  onClick={() => setIsExportModalOpen(false)}
                  className="w-8 h-8 p-0"
                />
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* File Name */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  File Name
                </label>
                <Input
                  type="text"
                  value={exportName}
                  onChange={(e) => setExportName(e.target.value)}
                  placeholder="Enter file name"
                  className="w-full"
                />
              </div>

              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Format
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {exportFormats.map((format) => (
                    <button
                      key={format.id}
                      onClick={() => setExportFormat(format.id)}
                      className={`
                        p-3 rounded-lg border transition-cosmic text-left
                        ${exportFormat === format.id
                          ? 'bg-primary/20 text-primary border-primary/30' :'bg-surface/30 text-text-secondary border-border hover:bg-surface/50'
                        }
                      `}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        <Icon name={format.icon} size={16} color="currentColor" />
                        <span className="text-sm font-medium">{format.label}</span>
                      </div>
                      <p className="text-xs opacity-80">{format.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quality Selection */}
              {exportFormat !== 'midi' && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Quality
                  </label>
                  <div className="space-y-2">
                    {qualityOptions.map((quality) => (
                      <button
                        key={quality.id}
                        onClick={() => setExportQuality(quality.id)}
                        className={`
                          w-full p-3 rounded-lg border transition-cosmic text-left
                          ${exportQuality === quality.id
                            ? 'bg-primary/20 text-primary border-primary/30' :'bg-surface/30 text-text-secondary border-border hover:bg-surface/50'
                          }
                        `}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{quality.label}</span>
                          <span className="text-xs opacity-80">{quality.size}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata Option */}
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-text-primary">Include Metadata</span>
                  <p className="text-xs text-text-secondary">Astrological data and timestamps</p>
                </div>
                <button
                  onClick={() => setIncludeMetadata(!includeMetadata)}
                  className={`
                    w-12 h-6 rounded-full transition-cosmic relative
                    ${includeMetadata ? 'bg-primary' : 'bg-surface'}
                  `}
                >
                  <div className={`
                    w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform
                    ${includeMetadata ? 'translate-x-6' : 'translate-x-0.5'}
                  `} />
                </button>
              </div>

              {/* Export Progress */}
              {isExporting && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-text-primary">Exporting...</span>
                    <span className="text-xs text-text-secondary font-mono">{exportProgress}%</span>
                  </div>
                  <div className="w-full h-2 bg-surface rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-200"
                      style={{ width: `${exportProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-border/50">
              <div className="flex space-x-3">
                <Button
                  variant="ghost"
                  onClick={() => setIsExportModalOpen(false)}
                  fullWidth
                  disabled={isExporting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleExportStart}
                  fullWidth
                  disabled={isExporting || !exportName}
                  loading={isExporting}
                >
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Options Dropdown */}
      <div className="relative group">
        <Button
          variant="ghost"
          iconName="MoreHorizontal"
          className="w-10 h-10 p-0"
        />
        <div className="absolute right-0 top-full mt-2 w-48 glass bg-surface/95 backdrop-blur-cosmic rounded-lg shadow-elevation-4 border border-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-dropdown">
          <div className="p-2">
            {shareOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => handleShare(option.id)}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left hover:bg-surface/50 transition-cosmic"
              >
                <Icon name={option.icon} size={16} color="currentColor" />
                <span className="text-sm text-text-primary">{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportControls;